// noinspection JSVoidFunctionReturnValueUsed

import { useEffect, useRef, useState } from 'react';

// Third Party Integrations
import TemplateBuilderSDK from '@openlettermarketing/olc-react-sdk';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// Actions
import { failure, success } from '~/redux/actions/snackbar-actions.js';
import {
  deleteQR,
  fetchAllQR,
  getOneTemplate,
  getTemplatesByTab,
  updateQRStatus,
  updateQR,
  uploadQR,
} from '~/redux/actions/template-builder.js';
import {
  getOneDesignerTemplateQuery,
  updateTemplateQuery,
} from '~/redux/actions/admin-custom-designer-query.js';
import { fetchCustomFieldsForDesignerQuery } from '~/redux/actions/customFields-actions';
import {
  createPhoneTrackingNumber,
  fetchPhoneTrackingNumbers,
  lookupPhoneNumbers,
} from '~/redux/actions/phone-tracking-actions';

// Utils
import { getUserToken } from '~/utils/auth.js';
import { ENGAGEMENT_TYPES } from '~/utils/constants';

// Components
import Loader from '~/components/General/Loader';

// Styles
import './styles.scss';

const TemplateBuilder = () => {
  let { id } = useParams();
  const templateBuilderInstanceRef = useRef(null);
  const isSandboxMode = import.meta.env.VITE_APP_ENV !== 'production';
  const currentTheme = 'v2';
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isPropertyAddressEnabled = useSelector(
    (state) =>
      state?.generalSettingsReducers?.generalSettings?.propertyAddressStrictness === 'Include' ||
      false
  );

  const redirectToOlc = () => {
    destroyTemplateBuilder();
    navigate('/admin/designer-queries');
  };

  /**
   * @param {FormData} formData
   */
  const onUpdate = async (formData) => {
    await updateTemplateQuery(id, formData);
    redirectToOlc();
  };

  const setEditingTemplate = async (isMounted = () => true) => {
    try {
      setIsLoading(true);
      const response = await getOneDesignerTemplateQuery(id);

      if (!isMounted()) return;

      const queryData = response?.data?.data ?? {};
      const { isUpdated = false } = queryData;

      if (queryData.template) {
        createTemplateBuilder(queryData.template, isUpdated, queryData);
      } else {
        redirectToOlc();
      }
    } catch (error) {
      if (!isMounted()) return;
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      redirectToOlc();
    } finally {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  };

  const getAllTemplatesByTab = async (payload) => {
    try {
      const tempates = await getTemplatesByTab(payload);
      if (tempates.status === 200) {
        return tempates?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const getCustomFields = async () => {
    const customFields = await dispatch(
      fetchCustomFieldsForDesignerQuery({
        customTemplateId: id,
        pageSize: 500,
      })
    );
    if (customFields?.data?.data?.rows) {
      return customFields?.data?.data?.rows;
    } else {
      return [];
    }
  };

  const getAllQRCodes = async (payload) => {
    try {
      const qr = await fetchAllQR({
        ...payload,
        customTemplateId: id,
      });
      if (qr.status === 200) {
        return qr?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const getAllPurls = (payload) =>
    getAllQRCodes({
      ...payload,
      engagementType: ENGAGEMENT_TYPES.PURL,
    });

  const deleteQRCode = async (payload) => {
    try {
      const qr = await deleteQR({
        id: payload,
        customTemplateId: id,
      });
      if (qr.status === 200) {
        dispatch(success(qr?.data?.message || 'QR Code deleted successfully.'));
        return qr?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const uploadQRCode = async (payload) => {
    try {
      const qr = await uploadQR({
        ...payload,
        customTemplateId: id,
      });
      if (qr.status === 200 || qr.status === 201) {
        dispatch(success(qr?.data?.message || 'QR Code created successfully.'));
        return qr?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const createPurl = async (payload) => {
    return uploadQR({
      ...payload,
      engagementType: ENGAGEMENT_TYPES.PURL,
    });
  };

  const updatePurl = async (payload) => {
    return updateQR({
      ...payload,
      customTemplateId: id,
      engagementType: ENGAGEMENT_TYPES.PURL,
    });
  };

  const updatePurlStatus = async (purlId, status) => {
    return updateQRStatus({
      id: purlId,
      status,
      customTemplateId: id,
    });
  };

  const updateQRCode = async (payload) => {
    try {
      const qr = await updateQR({
        ...payload,
        customTemplateId: id,
      });
      if (qr.status === 200) {
        dispatch(success(qr?.data?.message || 'QR Code updated successfully.'));
        return qr?.data?.data;
      } else {
        return null;
      }
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const getTrackingPhoneNumbers = async (payload) => {
    try {
      const phoneNumbers = await dispatch(
        fetchPhoneTrackingNumbers({
          pagination: {
            page: payload?.page,
            pageSize: payload?.pageSize,
          },
          customTemplateId: id,
        })
      );
      if (phoneNumbers.status === 200) {
        return phoneNumbers?.data?.data;
      }
      return null;
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const lookupTrackingPhoneNumbers = async (payload) => {
    try {
      return await dispatch(lookupPhoneNumbers(payload));
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const createTrackingPhone = async (payload) => {
    try {
      const response = await dispatch(
        createPhoneTrackingNumber({
          ...payload,
          customTemplateId: id,
        })
      );
      if (response?.status === 200 || response?.status === 201) {
        dispatch(success(response?.data?.message || 'Tracking Phone Number created successfully.'));
      }
      return response;
    } catch (error) {
      dispatch(failure(error?.data?.message || 'Internal Server Error'));
      return null;
    }
  };

  const getTemplateById = async (_id) => {
    try {
      const tempate = await getOneTemplate(_id);
      if (tempate.status === 200) {
        return tempate?.data?.data;
      }
    } catch (error) {
      console.log(error);
      dispatch(failure(error?.data?.message || 'Internal Server Error@@@@'));
      return false;
    }
  };

  const destroyTemplateBuilder = async () => {
    await templateBuilderInstanceRef.current?.destroy?.();
    templateBuilderInstanceRef.current = null;
  };

  const createTemplateBuilder = (editingTemplateData = null, isUpdated = false, queryData = {}) => {
    templateBuilderInstanceRef.current?.destroy?.();
    const isDefaultPlan = queryData.isDefaultPlan === true;
    const isQrScanNotificationEnabled = isDefaultPlan
      ? false
      : (queryData.qrScanNotificationEnabled ?? queryData.isDefaultPlan === false);

    const props = {
      container: document.getElementById('template-builder-container'),
      secretKey: import.meta.env.VITE_APP_PLOTNO_API_KEY,
      publicApiKey: getUserToken(),
      platformName: 'OLC',
      templateBuilderRoute: `admin/designer-queries/template/${id}`,
      allowPropertyFields: isPropertyAddressEnabled,
      allowSenderFields: true,
      templateGalleryModal: true,
      sandbox: isSandboxMode,
      allowedAddOns: isPropertyAddressEnabled ? ['gsv', 'property_offer'] : [],
      allowAdditionalPage: true,
      enableDynamicQRCodeUrl: true,
      enablePurlTracking: true,
      purlBaseUrl: import.meta.env.VITE_APP_PURL_BASE_URL,
      env: import.meta.env.VITE_APP_ENV,
      currentTheme,
      onReturnAndNavigate: redirectToOlc,
      onGetTemplates: getAllTemplatesByTab,
      onGetCustomFields: getCustomFields,
      onGetQRCodes: getAllQRCodes,
      onDeleteQRCodes: deleteQRCode,
      onUploadQRCode: uploadQRCode,
      onEditQRCode: updateQRCode,
      onGetPURLs: getAllPurls,
      onCreatePURL: createPurl,
      onEditPURL: updatePurl,
      onUpdatePURLStatus: updatePurlStatus,
      onGetTrackingPhoneNumbers: getTrackingPhoneNumbers,
      onLookupTrackingPhoneNumbers: lookupTrackingPhoneNumbers,
      onCreateTrackingPhoneNumber: createTrackingPhone,
      onGetOneTemplate: getTemplateById,
      onSubmit: editingTemplateData ? onUpdate : () => {},
    };

    props.showQrScanNotificationFields = true;
    props.defaultQrNotificationEmail =
      queryData.defaultQrNotificationEmail || queryData.ownerEmail || queryData.email || '';
    props.qrScanNotificationEnabled = isQrScanNotificationEnabled;

    const hasSavedTemplateAssets = Boolean(
      editingTemplateData?.templateUrl && editingTemplateData?.thumbnailUrl
    );

    if (isUpdated || hasSavedTemplateAssets) {
      props.olcTemplate = editingTemplateData;
    } else {
      props.designerTemplateQuery = editingTemplateData;
    }

    templateBuilderInstanceRef.current = new TemplateBuilderSDK(props);
  };

  useEffect(() => {
    let isMounted = true;

    if (id) {
      setEditingTemplate(() => isMounted);
    }

    return () => {
      isMounted = false;
      destroyTemplateBuilder();
    };
  }, [id]);

  return (
    <div className="adminDesignerTemplateBuilder">
      {isLoading && (
        <div className="adminDesignerTemplateBuilderLoader">
          <Loader />
        </div>
      )}
      <div id="template-builder-container" />
    </div>
  );
};

export default TemplateBuilder;
